import { memo, useCallback, useMemo, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ImageWithFallback } from "@/app/components/figma/ImageWithFallback";
import { Dialog, DialogContent, DialogTitle } from "@/app/components/ui/dialog";
import type { PromoCarouselSlideConfig } from "@/app/data/default-promo-slides";
import { DEFAULT_PROMO_SLIDES } from "@/app/data/default-promo-slides";
import { filterPromoSlidesForDisplay } from "@/app/utils/promo-carousel-filter";
import {
	APPSTORE_DEEPLINK_URL,
	APPSTORE_URL,
	PLAYSTORE_DEEPLINK_URL,
	PLAYSTORE_URL,
} from "@/config/store-urls";
import { QRCodeSVG } from "qrcode.react";

export type { PromoCarouselSlideConfig } from "@/app/data/default-promo-slides";

interface PromoCarouselProps {
	/** From `ezyhub/mobile_shell` (`promo_carousel.props.slides`); omit or undefined until first load → built-in default. */
	slides?: PromoCarouselSlideConfig[];
	onCategoryClick?: (categoryId: string) => void;
	onNavigateToTrocaViaHub?: () => void;
	/** `null` = catalog not loaded yet (only Troca-via-Hub slide shown). */
	validCatalogCategoryIds?: Set<string> | null;
}

export const PromoCarousel = memo(function PromoCarousel({
	slides: slidesProp,
	onCategoryClick,
	onNavigateToTrocaViaHub,
	validCatalogCategoryIds = null,
}: PromoCarouselProps) {
	const baseSlides = slidesProp ?? DEFAULT_PROMO_SLIDES;
	const [appDownloadOpen, setAppDownloadOpen] = useState(false);

	const visibleSlides = useMemo(
		() =>
			filterPromoSlidesForDisplay(
				baseSlides,
				validCatalogCategoryIds ?? null,
				"web",
			),
		[baseSlides, validCatalogCategoryIds],
	);

	const settings = useMemo(
		() => ({
			dots: true,
			infinite: true,
			speed: 500,
			slidesToShow: 1,
			slidesToScroll: 1,
			autoplay: true,
			autoplaySpeed: 4000,
			pauseOnHover: true,
			arrows: true,
			lazyLoad: "ondemand" as const,
		}),
		[],
	);

	const handleSlideClick = useCallback(
		(slide: PromoCarouselSlideConfig) => {
			if (slide.action === "open-troca-via-hub") {
				onNavigateToTrocaViaHub?.();
				return;
			}
			if (slide.action === "open-app-download") {
				setAppDownloadOpen(true);
				return;
			}
			onCategoryClick?.(slide.category_id);
		},
		[onCategoryClick, onNavigateToTrocaViaHub],
	);

	const ctaLabelFor = (slide: PromoCarouselSlideConfig): string => {
		if (slide.ctaLabel) return slide.ctaLabel;
		if (slide.action === "open-troca-via-hub") return "Saiba mais";
		if (slide.action === "open-app-download") return "Baixar agora";
		return `Ver ${slide.category}`;
	};

	if (visibleSlides.length === 0) return null;

	return (
		<>
			<div className="promo-carousel w-full mb-8">
				<Slider {...settings}>
					{visibleSlides.map((slide) => {
						const categoryTrim = slide.category.trim();
						const showCategoryChip = categoryTrim.length > 0;

						return (
							<div key={slide.id} className="overflow-visible pb-1">
								<div className="relative mt-4 h-64 md:h-80 rounded-2xl">
									<div
										className={`pointer-events-none absolute inset-0 rounded-2xl overflow-hidden ${slide.backgroundColor}`}
										style={
											slide.backgroundStyle
												? { background: slide.backgroundStyle }
												: undefined
										}
									/>
									{showCategoryChip ? (
										<div className="absolute left-6 top-0 z-20 -translate-y-1/2 px-3.5 py-1.5 rounded-full bg-white/25 text-white text-xs font-semibold uppercase tracking-wide shadow-lg ring-1 ring-white/40 backdrop-blur-sm">
											{slide.category}
										</div>
									) : null}
									<div className="absolute inset-0 flex items-center">
										<div className="w-full md:w-1/2 px-8 md:px-12 z-10">
											<h2 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-3 drop-shadow-sm">
												{slide.title}
											</h2>
											<p className="text-white/90 text-lg mb-6">
												{slide.subtitle}
											</p>
											<button
												type="button"
												className="px-6 py-3 bg-white text-primary rounded-lg hover:bg-white/90 transition-colors shadow-md"
												onClick={() => handleSlideClick(slide)}
											>
												{ctaLabelFor(slide)}
											</button>
										</div>
										<div className="hidden md:block absolute right-0 top-0 bottom-0 w-1/2">
											<ImageWithFallback
												src={slide.imageUrl}
												alt={slide.title}
												className="w-full h-full object-cover opacity-40"
												loading="lazy"
											/>
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</Slider>
			</div>

			{/* App download picker — opened from slides with action === "open-app-download". */}
			<Dialog open={appDownloadOpen} onOpenChange={setAppDownloadOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogTitle className="text-center">Baixe o app akihub</DialogTitle>
					<p className="text-center text-sm text-muted-foreground">
						Escolha a sua loja para continuar.
					</p>
					<div className="mt-2 flex flex-col sm:flex-row items-stretch justify-center gap-6 sm:gap-8">
						<div className="flex flex-col items-center gap-3">
							<a
								href={APPSTORE_URL}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Baixar na App Store"
								className="inline-flex items-center transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
								onClick={() => setAppDownloadOpen(false)}
							>
								<img
									src="/badges/app-store-badge-pt-br.svg"
									alt="Baixar na App Store"
									className="h-12 w-auto"
									loading="lazy"
									decoding="async"
								/>
							</a>
							<div
								className="rounded-md border border-border bg-white p-2"
								aria-label="QR code para baixar na App Store"
							>
								<QRCodeSVG
									value={APPSTORE_DEEPLINK_URL}
									size={128}
									level="M"
									includeMargin={false}
								/>
							</div>
							<span className="text-xs text-muted-foreground">Aponte a câmera</span>
						</div>
						<div className="flex flex-col items-center gap-3">
							<a
								href={PLAYSTORE_URL}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Disponível no Google Play"
								className="inline-flex items-center transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
								onClick={() => setAppDownloadOpen(false)}
							>
								<img
									src="/badges/google-play-badge-pt-br.png"
									alt="Disponível no Google Play"
									className="h-12 w-auto"
									loading="lazy"
									decoding="async"
								/>
							</a>
							<div
								className="rounded-md border border-border bg-white p-2"
								aria-label="QR code para baixar no Google Play"
							>
								<QRCodeSVG
									value={PLAYSTORE_DEEPLINK_URL}
									size={128}
									level="M"
									includeMargin={false}
								/>
							</div>
							<span className="text-xs text-muted-foreground">Aponte a câmera</span>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
});
